import { validateSourceRegionManifest, type SourceRegionManifest } from '../lib/psd-source-regions'
import type { ScreenBrandingView } from '@yihang/contracts'
import type {
  ActivationResult,
  AutoSelectionCandidate,
  AutoSelectionCandidateQuery,
  AutoSelectionAutomationSettings,
  AutoSelectionConfig,
  AutoSelectionRules,
  AutoSelectionRun,
  AiProviderConfigView,
  AiPointBalanceView,
  AiRewriteLogPage,
  AiRewriteProductPage,
  AiProviderConfigWrite,
  AiProviderRouteKind,
  AiImageGenerationPlan,
  AiRewriteMode,
  AiRewriteScope,
  AiSettingsView,
  ApiProblem,
  AsyncAccepted,
  AuthSession,
  BulkOrderLineMappingItem,
  BulkOrderLineMappingResult,
  BulkOrderLineSourceItem,
  BulkOrderLineSourceResult,
  DashboardSummary,
  CollectionStatsSummary,
  DataScreenRange,
  DataScreenSummary,
  Device,
  Job,
  ListingGroup,
  ListingGroupDetail,
  Offer,
  OfferSkuBatchDeleteResult,
  OfferUpdate,
  OzonCategoryAttribute,
  OzonCategoryAttributeValuePage,
  OzonDescriptionCategoryNode,
  OzonMetadataCollection,
  Order,
  OrderSourceOrigin,
  Page,
  PairingTicket,
  PasswordResetRequestResult,
  PricingCalculatorInput,
  PricingRule,
  ProductDetail,
  ProductPoolItem,
  ProfitCalculatorInput,
  MarketplaceCalculatorResult,
  Settings,
  SeerfarCategoryCatalog,
  SeerfarCredentialView,
  Shop,
  StoreCollection,
  StoreCollectionProduct,
  StoreCollectionProductQuery,
  StoreCollectionRun,
  TenantSubAccount,
  TenantSubAccountPage,
  AccountListingStats,
  AiPointLedgerItem,
  TenantNotification,
  ValidationCommand,
  ValidationResult,
} from './types'
import {
  orderFinanceSummaryPath,
  type OrderFinanceFilters,
  type FilteredOrderFinanceSummary,
} from './order-finance-summary'

const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
export const API_BASE_URL = (configuredBaseUrl || 'http://127.0.0.1:3000/v1').replace(/\/+$/, '')

type ProblemListener = (problem: ApiProblem) => void
const problemListeners = new Set<ProblemListener>()
let refreshSessionRequest: Promise<boolean> | null = null

// A single-host rollout briefly removes the old API container before the new
// one is ready. Reads must absorb that short gateway window instead of
// surfacing a misleading 502 banner to the operator. Writes are deliberately
// excluded: even an idempotency key cannot make every endpoint safe to replay.
const TRANSIENT_READ_RETRY_DELAYS_MS = [250, 750, 1_500, 2_500] as const
const TRANSIENT_GATEWAY_STATUSES = new Set([502, 503, 504])

export class ApiError extends Error {
  constructor(readonly problem: ApiProblem) {
    super(problem.message)
    this.name = 'ApiError'
  }
}

export interface ApiResponse<T> {
  data: T
  etag: string | null
  correlationId: string | null
}

export interface StandaloneImageJob {
  id: string
  status: string
  prompt: string
  sourceImageUrl: string
  createdAt: string
  result: { imageUrl?: string; pointsConsumed?: number; error?: string } | null
}

export interface TenantLoginInput {
  email: string
  password: string
  totp?: string
}

export interface TenantPasswordResetRequestInput {
  email: string
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  rawBody?: BodyInit
  contentType?: string
  announceError?: boolean
  idempotencyKey?: string
  ifMatch?: string
  retryAuth?: boolean
}

function uuid() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function fallbackProblem(response: Response, correlationId: string): ApiProblem {
  return {
    type: 'about:blank',
    title: response.statusText || '请求失败',
    status: response.status,
    code: `HTTP_${response.status}`,
    message: response.statusText || `请求失败（HTTP ${response.status}）`,
    correlationId,
    retryable: response.status >= 500,
  }
}

function isProblem(value: unknown): value is ApiProblem {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<ApiProblem>
  return (
    typeof candidate.status === 'number' &&
    typeof candidate.code === 'string' &&
    typeof candidate.message === 'string' &&
    typeof candidate.correlationId === 'string' &&
    typeof candidate.retryable === 'boolean'
  )
}

function announce(problem: ApiProblem) {
  problemListeners.forEach((listener) => listener(problem))
}

function refreshSessionOnce() {
  if (!refreshSessionRequest) {
    refreshSessionRequest = restoreCookieSession()
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        refreshSessionRequest = null
      })
  }
  return refreshSessionRequest
}

function isRetryableRead(options: RequestOptions): boolean {
  const method = (options.method ?? 'GET').toUpperCase()
  return (method === 'GET' || method === 'HEAD') && options.signal?.aborted !== true
}

function waitForTransientRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs)
  })
}

async function fetchWithTransientReadRetry(
  input: RequestInfo | URL,
  init: RequestInit,
  options: RequestOptions,
): Promise<Response> {
  const retryRead = isRetryableRead(options)
  let attempt = 0
  while (true) {
    try {
      const response = await fetch(input, init)
      if (
        !retryRead ||
        !TRANSIENT_GATEWAY_STATUSES.has(response.status) ||
        attempt >= TRANSIENT_READ_RETRY_DELAYS_MS.length
      )
        return response

      // Release the transient error response before retrying so the browser
      // does not keep an idle gateway connection occupied.
      await response.body?.cancel().catch(() => undefined)
      await waitForTransientRetry(TRANSIENT_READ_RETRY_DELAYS_MS[attempt]!)
      attempt += 1
    } catch (cause) {
      const aborted = options.signal?.aborted === true
      const isAbort = cause instanceof DOMException && cause.name === 'AbortError'
      if (!retryRead || aborted || isAbort || attempt >= TRANSIENT_READ_RETRY_DELAYS_MS.length)
        throw cause
      await waitForTransientRetry(TRANSIENT_READ_RETRY_DELAYS_MS[attempt]!)
      attempt += 1
    }
  }
}

async function withTenantSessionLock<T>(operation: () => Promise<T>): Promise<T> {
  const locks = globalThis.navigator?.locks
  if (!locks) return operation()
  const result = await locks.request(
    'yihang-tenant-session-refresh',
    { mode: 'exclusive' },
    operation,
  )
  return result
}

function restoreCookieSession(): Promise<Partial<AuthSession>> {
  return withTenantSessionLock(async () => {
    try {
      return (
        await rawRequest<Partial<AuthSession>>('/auth/session', {
          announceError: false,
          retryAuth: false,
        })
      ).data
    } catch (error) {
      if (!(error instanceof ApiError) || error.problem.status !== 401) throw error
    }

    return (
      await rawRequest<Partial<AuthSession>>('/auth/refresh', {
        announceError: false,
        method: 'POST',
        retryAuth: false,
      })
    ).data
  })
}

export async function rawRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const correlationId = uuid()
  const headers = new Headers(options.headers)
  headers.set('accept', 'application/json')
  headers.set('x-correlation-id', correlationId)
  headers.set('x-app-surface', 'tenant')
  if (options.body !== undefined) headers.set('content-type', 'application/json')
  else if (options.rawBody !== undefined)
    headers.set('content-type', options.contentType ?? 'application/octet-stream')
  if (options.idempotencyKey) headers.set('idempotency-key', options.idempotencyKey)
  if (options.ifMatch) headers.set('if-match', options.ifMatch)
  const mockLicenseState = import.meta.env.VITE_MOCK_LICENSE_STATE as string | undefined
  if (mockLicenseState) headers.set('x-mock-license-state', mockLicenseState)

  let response: Response
  try {
    response = await fetchWithTransientReadRetry(
      `${API_BASE_URL}${path}`,
      {
        ...options,
        body: options.body !== undefined ? JSON.stringify(options.body) : options.rawBody,
        credentials: 'include',
        headers,
      },
      options,
    )
  } catch (cause) {
    const problem: ApiProblem = {
      type: 'about:blank',
      title: '无法连接服务',
      status: 503,
      code: 'NETWORK_ERROR',
      message: `无法连接 ${API_BASE_URL}，请确认 API 服务已启动。`,
      detail: cause instanceof Error ? cause.message : undefined,
      correlationId,
      retryable: true,
    }
    if (options.announceError !== false) announce(problem)
    throw new ApiError(problem)
  }

  if (response.status === 401 && options.retryAuth !== false && path !== '/auth/refresh') {
    const refreshed = await refreshSessionOnce()
    if (refreshed) return rawRequest<T>(path, { ...options, retryAuth: false })
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as unknown
    const problem = isProblem(body) ? body : fallbackProblem(response, correlationId)
    if (options.announceError !== false) announce(problem)
    throw new ApiError(problem)
  }

  const data = response.status === 204 ? (undefined as T) : ((await response.json()) as T)
  return {
    data,
    etag: response.headers.get('etag'),
    correlationId: response.headers.get('x-correlation-id'),
  }
}

/**
 * Binary tenant reads use the same cookie/session and transient gateway
 * handling as JSON requests.  Image editor source bytes must be fetched from
 * our authenticated API, not loaded directly from supplier/CDN origins that
 * commonly deny Canvas CORS access.
 */
async function rawBlobRequest(path: string, options: RequestOptions = {}): Promise<Blob> {
  const correlationId = uuid()
  const {
    body: _body,
    rawBody: _rawBody,
    contentType: _contentType,
    announceError: _announceError,
    idempotencyKey: _idempotencyKey,
    ifMatch: _ifMatch,
    retryAuth: _retryAuth,
    ...fetchOptions
  } = options
  const headers = new Headers(options.headers)
  if (!headers.has('accept')) headers.set('accept', 'image/png, image/jpeg, image/webp')
  headers.set('x-correlation-id', correlationId)
  headers.set('x-app-surface', 'tenant')
  const mockLicenseState = import.meta.env.VITE_MOCK_LICENSE_STATE as string | undefined
  if (mockLicenseState) headers.set('x-mock-license-state', mockLicenseState)

  let response: Response
  try {
    response = await fetchWithTransientReadRetry(
      `${API_BASE_URL}${path}`,
      { ...fetchOptions, credentials: 'include', headers },
      options,
    )
  } catch (cause) {
    const problem: ApiProblem = {
      type: 'about:blank',
      title: '无法连接服务',
      status: 503,
      code: 'NETWORK_ERROR',
      message: `无法连接 ${API_BASE_URL}，请确认 API 服务已启动。`,
      detail: cause instanceof Error ? cause.message : undefined,
      correlationId,
      retryable: true,
    }
    if (options.announceError !== false) announce(problem)
    throw new ApiError(problem)
  }

  if (response.status === 401 && options.retryAuth !== false && path !== '/auth/refresh') {
    const refreshed = await refreshSessionOnce()
    if (refreshed) return rawBlobRequest(path, { ...options, retryAuth: false })
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as unknown
    const problem = isProblem(body) ? body : fallbackProblem(response, correlationId)
    if (options.announceError !== false) announce(problem)
    throw new ApiError(problem)
  }
  return response.blob()
}

export type PsdLayerKind =
  'TEXT' | 'SUBJECT' | 'GRAPHICS' | 'LOGO' | 'WATERMARK' | 'BACKGROUND' | 'REFERENCE'

export interface ImageToPsdMetadata {
  sourceRegions?: SourceRegionManifest
  width: number
  height: number
  sourceWidth: number
  sourceHeight: number
  sourceSha256: string
  sourceSelection?: {
    mode: 'PROVIDED' | 'HIGHER_RESOLUTION'
    providedWidth: number
    providedHeight: number
    selectedWidth: number
    selectedHeight: number
    comparisonMeanError?: number
    comparisonWorstPatchError?: number
    reason?: 'NO_VARIANT' | 'NO_SIZE_GAIN' | 'DIFFERENT_IMAGE' | 'VARIANT_UNAVAILABLE'
  }
  agentVersion:
    | 'semantic-split-v1'
    | 'vision-semantic-split-v2'
    | 'clean-layer-split-v3'
    | 'source-matte-split-v4'
    | 'subject-ai-matte-v5'
    | 'source-layout-matte-v6'
    | 'native-reconstruction-v7'
    | 'object-raster-v8'
    | 'unknown'
  analysisMode: 'VISION_AI' | 'HEURISTIC_FALLBACK'
  analysisConfidence?: number
  analysisEngine?: 'VISION_LANGUAGE' | 'IMAGE_AI'
  backgroundMode?: 'IMAGE_AI' | 'TRANSPARENT_GAPS'
  maskRefinement?: 'SOURCE_COMPONENTS' | 'SEMANTIC_MASK'
  mattingMode?: 'SOURCE_ALPHA' | 'BINARY_MASK'
  ocrWordCount?: number
  subjectAi?: {
    status: 'APPLIED' | 'REJECTED' | 'UNAVAILABLE'
    reason?: string
    maskIou?: number
    changedAlphaPixels?: number
  }
  imageAiOperations?: number
  textAiOperations?: number
  nativeCounts?: {
    texts: number
    smartObjects: number
    shapes: number
    decorations: number
    logos?: number
    watermarks?: number
  }
  quality?: {
    status: 'REVIEW_REQUIRED' | 'AUTOMATED_CHECKS_PASSED'
    textComplete: boolean
    layoutFaithful: boolean
    subjectClean: boolean
    backgroundClean: boolean
    categoriesIndependent?: boolean
    issues: string[]
  }
  warnings?: string[]
  layers: Array<{
    name: string
    kind: PsdLayerKind
    visible: boolean
    nativeType?: 'TEXT' | 'SMART_OBJECT' | 'SHAPE' | 'RASTER'
    group?: string
    elementGroup?: string
  }>
}

export interface ImageToPsdDownload {
  blob: Blob
  fileName: string
  metadata: ImageToPsdMetadata
  metadataError?: string
}

export async function decodePsdMetadata(value: string | null): Promise<ImageToPsdMetadata> {
  if (!value) throw new Error('PSD 已生成，但响应缺少图层清单')
  try {
    if (value.length > 64_000) throw new Error('PSD metadata limit')
    const compressed = value.startsWith('gz.')
    const normalized = (compressed ? value.slice(3) : value)
      .replaceAll('-', '+')
      .replaceAll('_', '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0))
    let json = new TextDecoder().decode(bytes)
    if (compressed) {
      const reader = new Blob([bytes])
        .stream()
        .pipeThrough(new DecompressionStream('gzip'))
        .getReader()
      const chunks: Uint8Array[] = []
      let total = 0
      while (true) {
        const item = await reader.read()
        if (item.done) break
        total += item.value.length
        if (total > 256_000) {
          await reader.cancel()
          throw new Error('PSD metadata limit')
        }
        chunks.push(item.value)
      }
      const decoded = new Uint8Array(total)
      let offset = 0
      for (const chunk of chunks) {
        decoded.set(chunk, offset)
        offset += chunk.length
      }
      json = new TextDecoder().decode(decoded)
    }
    const parsed = JSON.parse(json) as Partial<ImageToPsdMetadata> | null
    const strings = (list: unknown) =>
      Array.isArray(list) &&
      list.length <= 512 &&
      list.every((v) => typeof v === 'string' && v.length <= 8000)
    const positiveSize = (size: unknown) =>
      typeof size === 'number' && Number.isSafeInteger(size) && size > 0 && size <= 30_000
    const count = (n: unknown) =>
      typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 && n <= 100_000
    const optionalCount = (n: unknown) => n === undefined || count(n)
    const quality = parsed?.quality
    if (
      !parsed ||
      !positiveSize(parsed.width) ||
      !positiveSize(parsed.height) ||
      !Array.isArray(parsed.layers) ||
      parsed.layers.length > 512 ||
      parsed.layers.some(
        (layer) =>
          !layer ||
          typeof layer.name !== 'string' ||
          layer.name.length > 2000 ||
          typeof layer.visible !== 'boolean' ||
          !['TEXT', 'SUBJECT', 'GRAPHICS', 'LOGO', 'WATERMARK', 'BACKGROUND', 'REFERENCE'].includes(
            layer.kind,
          ) ||
          (layer.nativeType !== undefined &&
            !['TEXT', 'SMART_OBJECT', 'SHAPE', 'RASTER'].includes(layer.nativeType)) ||
          (layer.group !== undefined && typeof layer.group !== 'string') ||
          (layer.elementGroup !== undefined && typeof layer.elementGroup !== 'string'),
      ) ||
      (parsed.warnings !== undefined && !strings(parsed.warnings)) ||
      (parsed.backgroundMode !== undefined &&
        !['IMAGE_AI', 'TRANSPARENT_GAPS'].includes(parsed.backgroundMode)) ||
      !optionalCount(parsed.imageAiOperations) ||
      !optionalCount(parsed.textAiOperations) ||
      !optionalCount(parsed.ocrWordCount) ||
      (quality !== undefined &&
        (!quality ||
          !['REVIEW_REQUIRED', 'AUTOMATED_CHECKS_PASSED'].includes(quality.status) ||
          !strings(quality.issues) ||
          [
            quality.textComplete,
            quality.layoutFaithful,
            quality.subjectClean,
            quality.backgroundClean,
          ].some((v) => typeof v !== 'boolean') ||
          (quality.categoriesIndependent !== undefined &&
            typeof quality.categoriesIndependent !== 'boolean'))) ||
      (parsed.nativeCounts !== undefined &&
        (!parsed.nativeCounts ||
          [
            parsed.nativeCounts.texts,
            parsed.nativeCounts.smartObjects,
            parsed.nativeCounts.shapes,
            parsed.nativeCounts.decorations,
          ].some((v) => !count(v)) ||
          !optionalCount(parsed.nativeCounts.logos) ||
          !optionalCount(parsed.nativeCounts.watermarks))) ||
      (parsed.sourceWidth !== undefined && !positiveSize(parsed.sourceWidth)) ||
      (parsed.sourceHeight !== undefined && !positiveSize(parsed.sourceHeight)) ||
      (parsed.subjectAi !== undefined &&
        (!parsed.subjectAi ||
          !['APPLIED', 'REJECTED', 'UNAVAILABLE'].includes(parsed.subjectAi.status) ||
          (parsed.subjectAi.reason !== undefined && typeof parsed.subjectAi.reason !== 'string')))
    )
      throw new Error('Invalid PSD metadata')
    if (parsed.sourceRegions !== undefined)
      parsed.sourceRegions = validateSourceRegionManifest(parsed.sourceRegions)
    return parsed as ImageToPsdMetadata
  } catch (cause) {
    throw new Error('PSD 已生成，但图层清单无法解析', { cause })
  }
}

async function rawPsdRequest(
  path: string,
  input: {
    imageUrl: string
    maxSize: 1_024 | 2_048
    subjectAiRefine?: boolean
    outputMode?: 'RASTER' | 'NATIVE'
    instructions?: string
  },
  signal?: AbortSignal,
  retryAuth = true,
): Promise<ImageToPsdDownload> {
  const correlationId = uuid()
  const headers = new Headers({
    accept: 'image/vnd.adobe.photoshop',
    'content-type': 'application/json',
    'x-correlation-id': correlationId,
    'x-app-surface': 'tenant',
  })
  const mockLicenseState = import.meta.env.VITE_MOCK_LICENSE_STATE as string | undefined
  if (mockLicenseState) headers.set('x-mock-license-state', mockLicenseState)

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      body: JSON.stringify(input),
      credentials: 'include',
      headers,
      signal,
    })
  } catch (cause) {
    if (signal?.aborted) throw new DOMException('PSD generation cancelled', 'AbortError')
    const problem: ApiProblem = {
      type: 'about:blank',
      title: '无法连接服务',
      status: 503,
      code: 'NETWORK_ERROR',
      message: `无法连接 ${API_BASE_URL}，请确认 API 服务已启动。`,
      detail: cause instanceof Error ? cause.message : undefined,
      correlationId,
      retryable: true,
    }
    announce(problem)
    throw new ApiError(problem)
  }

  if (response.status === 401 && retryAuth) {
    const refreshed = await refreshSessionOnce()
    if (refreshed) return rawPsdRequest(path, input, signal, false)
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as unknown
    const problem = isProblem(body) ? body : fallbackProblem(response, correlationId)
    announce(problem)
    throw new ApiError(problem)
  }
  const blob = await response.blob()
  const fileName = response.headers.get('x-psd-file-name') ?? 'image-layers.psd'
  let metadata: ImageToPsdMetadata
  let metadataError: string | undefined
  try {
    metadata = await decodePsdMetadata(response.headers.get('x-psd-metadata'))
  } catch (error) {
    // A damaged optional header must never discard a paid binary result.
    metadataError = error instanceof Error ? error.message : '图层清单无法解析'
    const header = new DataView(await blob.slice(0, 26).arrayBuffer())
    const valid = header.byteLength === 26 && header.getUint32(0) === 0x38425053
    const width = valid ? header.getUint32(18) : 0,
      height = valid ? header.getUint32(14) : 0
    metadata = {
      width,
      height,
      sourceWidth: width,
      sourceHeight: height,
      sourceSha256: '',
      agentVersion: 'unknown',
      analysisMode: 'HEURISTIC_FALLBACK',
      layers: [],
      warnings: [metadataError + '；已保留原始 PSD，请以文件内图层为准。'],
    }
  }
  return {
    blob,
    fileName,
    metadata,
    ...(metadataError ? { metadataError } : {}),
  }
}

export function onApiProblem(listener: ProblemListener) {
  problemListeners.add(listener)
  return () => {
    problemListeners.delete(listener)
  }
}

export function idempotencyKey(scope: string) {
  return `${scope}-${uuid()}`
}

export function problemMessage(error: unknown) {
  if (error instanceof ApiError) {
    const field = error.problem.fieldPath ? `（字段：${error.problem.fieldPath}）` : ''
    return `${error.problem.message}${field} · 关联 ID ${error.problem.correlationId}`
  }
  return error instanceof Error ? error.message : '请求失败'
}

export const api = {
  request: rawRequest,
  get: <T>(path: string) => rawRequest<T>(path),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    rawRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string) => rawRequest<T>(path, { method: 'DELETE' }),
}

export const tenantApi = {
  login: (input: TenantLoginInput) =>
    api.post<AuthSession>('/auth/login', input, { retryAuth: false }),
  requestPasswordReset: (input: TenantPasswordResetRequestInput) =>
    api.post<PasswordResetRequestResult>('/auth/password-reset/request', input, {
      retryAuth: false,
    }),
  confirmPasswordReset: (input: { token: string; password: string }) =>
    api.post<void>('/auth/password-reset/confirm', input, { retryAuth: false }),
  activate: (input: {
    license: string
    adminName: string
    email: string
    password: string
    deviceName: string
  }) =>
    api.post<ActivationResult>('/auth/activate', input, {
      idempotencyKey: idempotencyKey('activate'),
      retryAuth: false,
    }),
  restoreSession: async () => {
    const restored = await restoreCookieSession()
    if (!restored.user || !restored.refreshExpiresAt) return null
    const session = restored as AuthSession
    return ['TENANT_ADMIN', 'TENANT_SUB_ACCOUNT'].includes(session.user.role) && session.tenant
      ? session
      : null
  },
  logout: () => api.post<void>('/auth/logout', undefined, { retryAuth: false }),
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    api.post<AuthSession>('/auth/change-password', input),
  calculatePricing: (input: PricingCalculatorInput) =>
    api.post<MarketplaceCalculatorResult>('/calculators/pricing', input),
  calculateProfit: (input: ProfitCalculatorInput) =>
    api.post<MarketplaceCalculatorResult>('/calculators/profit', input),
  currencyRate: () =>
    api.get<{ code: number; message?: string; data?: number }>('/calculators/currency-rate'),
  imageToPsd: (
    input: {
      imageUrl: string
      maxSize: 1_024 | 2_048
      subjectAiRefine?: boolean
      outputMode?: 'RASTER' | 'NATIVE'
      instructions?: string
    },
    signal?: AbortSignal,
  ) => rawPsdRequest('/image-to-psd', input, signal),
  dashboard: (range: DashboardSummary['business']['range'] = '30d', shopId?: string) => {
    const search = new URLSearchParams({ range })
    if (shopId) search.set('shopId', shopId)
    return api.get<DashboardSummary>(`/dashboard?${search.toString()}`)
  },
  collectionStats: (
    input: {
      range?: CollectionStatsSummary['range']
      start?: string
      end?: string
    } = {},
  ) => {
    const search = new URLSearchParams({ range: input.range ?? '7d' })
    if (input.start) search.set('start', input.start)
    if (input.end) search.set('end', input.end)
    return api.get<CollectionStatsSummary>(`/dashboard/collection-stats?${search.toString()}`)
  },
  dataScreen: (
    input: {
      range?: DataScreenRange
      start?: string
      end?: string
      shopId?: string
    } = {},
  ) => {
    const search = new URLSearchParams({ range: input.range ?? 'today' })
    if (input.start) search.set('start', input.start)
    if (input.end) search.set('end', input.end)
    if (input.shopId) search.set('shopId', input.shopId)
    return api.get<DataScreenSummary>(`/data-screen?${search.toString()}`)
  },
  shops: () => api.get<Page<Shop>>('/shops'),
  createShop: (input: {
    name: string
    clientId: string
    apiKey: string
    shippingTemplateId?: string
  }) => api.post<Shop>('/shops', input, { idempotencyKey: idempotencyKey('shop-create') }),
  verifyShop: (shopId: string) =>
    api.post<AsyncAccepted>(`/shops/${shopId}/verify`, undefined, {
      idempotencyKey: idempotencyKey('shop-verify'),
    }),
  archiveShop: (shopId: string) => api.delete<void>(`/shops/${shopId}`),
  updateShopName: (shopId: string, name: string, etag: string) =>
    api.patch<Shop>(`/shops/${shopId}/name`, { name }, { ifMatch: etag }),
  rotateShopCredential: (shopId: string, input: { clientId: string; apiKey: string }) =>
    api.post<AsyncAccepted>(`/shops/${shopId}/credentials/rotate`, input, {
      idempotencyKey: idempotencyKey('shop-rotate'),
    }),
  updateShopPricing: (shopId: string, pricing: Omit<PricingRule, 'version'> | null, etag: string) =>
    api.patch<Shop>(`/shops/${shopId}/pricing`, { pricing }, { ifMatch: etag }),
  updateShopVat: (shopId: string, vatRate: string, etag: string) =>
    api.patch<Shop>(`/shops/${shopId}/vat`, { vatRate }, { ifMatch: etag }),
  products: (search = '') =>
    api.get<Page<ProductPoolItem>>(
      `/product-pool${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    ),
  storeCollections: () => api.get<StoreCollection[]>('/store-collections'),
  currentStoreCollectionRun: () =>
    api.get<StoreCollectionRun | null>('/store-collections/runs/current'),
  storeCollectionProducts: (filters: StoreCollectionProductQuery = {}) => {
    const search = new URLSearchParams()
    search.set('page', String(filters.page ?? 1))
    search.set('pageSize', String(filters.pageSize ?? 20))
    if (filters.collectionId) search.set('collectionId', filters.collectionId)
    if (filters.search) search.set('search', filters.search)
    if (filters.status) search.set('status', filters.status)
    const query = search.toString()
    return api.get<{
      items: StoreCollectionProduct[]
      total: number
      page: number
      pageSize: number
    }>(`/store-collections/products${query ? `?${query}` : ''}`)
  },
  storeCollectionProgress: (ids: string[]) =>
    api.get<{
      items: Array<
        Pick<
          StoreCollectionProduct,
          'id' | 'status' | 'creativeStatus' | 'updatedAt' | 'lastError'
        > & { active: boolean }
      >
    }>(`/store-collections/products/progress?ids=${ids.join(',')}`),
  startStoreCollection: (storeUrl: string) =>
    api.post<{ run: StoreCollectionRun; storeUrl: string }>('/store-collections', { storeUrl }),
  stopStoreCollection: (runId: string) =>
    api.post<StoreCollectionRun>(`/store-collections/runs/${runId}/stop`),
  refreshStoreCollectionProduct: (id: string) =>
    api.post<{ run: StoreCollectionRun; storeUrl: string }>(
      `/store-collections/products/${id}/refresh`,
    ),
  autoSelectionConfig: () => api.get<AutoSelectionConfig>('/auto-selection/config'),
  seerfarCategories: (refresh = false) =>
    api.get<SeerfarCategoryCatalog>(`/auto-selection/categories${refresh ? '?refresh=true' : ''}`),
  updateAutoSelectionConfig: (rules: AutoSelectionRules) =>
    api.put<AutoSelectionConfig>('/auto-selection/config', rules),
  startAutoSelectionRun: (targetShopId: string) =>
    api.post<{ run: AutoSelectionRun; seerfarUrl: string }>('/auto-selection/runs', {
      targetShopId,
    }),
  currentAutoSelectionRun: () => api.get<AutoSelectionRun | null>('/auto-selection/runs/current'),
  autoSelectionCandidates: (filters: AutoSelectionCandidateQuery = {}) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value)
    }
    const query = params.toString()
    return api.get<Page<AutoSelectionCandidate>>(
      `/auto-selection/candidates${query ? `?${query}` : ''}`,
    )
  },
  collectAutoSelectionCandidate: (id: string) =>
    api.post<{ productPoolItemId: string; created: boolean }>(
      `/auto-selection/candidates/${id}/collect`,
    ),
  deleteAutoSelectionCandidate: (id: string) =>
    api.delete<void>(`/auto-selection/candidates/${id}`),
  seerfarSettings: () => api.get<SeerfarCredentialView>('/settings/seerfar'),
  updateSeerfarSettings: (input: { account: string; password: string }) =>
    api.put<SeerfarCredentialView>('/settings/seerfar', input),
  autoSelectionAutomationSettings: () =>
    api.get<AutoSelectionAutomationSettings>('/settings/auto-selection'),
  updateAutoSelectionAutomationSettings: (input: AutoSelectionAutomationSettings) =>
    api.put<AutoSelectionAutomationSettings>('/settings/auto-selection', input),
  product: (id: string) => api.get<ProductDetail>(`/product-pool/${id}`),
  reuseProductCreatives: (id: string, shopIds: string[]) =>
    api.post<{ groups: number; images: number; texts: number }>(
      `/product-pool/${id}/reuse-creatives`,
      { shopIds },
    ),
  prepareProductListing: (id: string, shopIds: string[]) =>
    api.post<AsyncAccepted>(
      `/product-pool/${id}/prepare-listing`,
      { shopIds },
      {
        idempotencyKey: idempotencyKey(`product-prepare-listing-${id}`),
      },
    ),
  deleteProduct: (id: string) => api.delete<void>(`/product-pool/${id}`),
  offers: () => api.get<Page<Offer>>('/offers'),
  offer: (id: string) => api.get<Offer>(`/offers/${id}`),
  updateOffer: (id: string, body: OfferUpdate, etag: string) =>
    api.patch<Offer>(`/offers/${id}`, body, { ifMatch: etag }),
  uploadOfferImage: (id: string, file: File, etag: string, replaceImageUrl?: string) =>
    api.request<Offer>(`/offers/${id}/images/upload`, {
      method: 'POST',
      rawBody: file,
      contentType: 'application/octet-stream',
      ifMatch: etag,
      headers: {
        'x-image-content-type': file.type,
        'x-image-file-name': encodeURIComponent(file.name),
        ...(replaceImageUrl ? { 'x-image-replace-url': encodeURIComponent(replaceImageUrl) } : {}),
      },
    }),
  readOfferImageForEditing: (id: string, imageUrl: string) =>
    rawBlobRequest(
      `/offers/${encodeURIComponent(id)}/images/editor-source?imageUrl=${encodeURIComponent(imageUrl)}`,
    ),
  deleteOfferSkus: (offerIds: string[]) =>
    api.post<OfferSkuBatchDeleteResult>('/offers/batch-delete', { offerIds }),
  autoMatchOzonOffer: (id: string, etag: string) =>
    api.post<Offer>(`/offers/${id}/ozon-auto-match`, undefined, {
      ifMatch: etag,
      idempotencyKey: idempotencyKey(`offer-ozon-auto-match-${id}`),
    }),
  rewriteOffer: (
    id: string,
    scope: AiRewriteScope,
    mode: AiRewriteMode,
    imagePlan?: AiImageGenerationPlan,
  ) =>
    api.post<AsyncAccepted>(
      `/offers/${id}/ai-rewrite`,
      { scope, mode, ...(imagePlan ? { imagePlan } : {}) },
      { idempotencyKey: idempotencyKey(`offer-ai-rewrite-${id}-${scope}`) },
    ),
  retryOfferTextRewrite: (id: string) =>
    api.post<AsyncAccepted>(`/offers/${id}/retry-text-rewrite`, undefined, {
      idempotencyKey: idempotencyKey(`offer-text-retry-${id}`),
    }),
  ozonCategories: (shopId?: string) =>
    api.get<OzonMetadataCollection<OzonDescriptionCategoryNode>>(
      `/ozon/categories${shopId ? `?shopId=${encodeURIComponent(shopId)}` : ''}`,
    ),
  ozonCategoryAttributes: (shopId: string, categoryId: string, typeId: string) =>
    api.get<OzonMetadataCollection<OzonCategoryAttribute>>(
      `/ozon/categories/${encodeURIComponent(categoryId)}/types/${encodeURIComponent(
        typeId,
      )}/attributes?shopId=${encodeURIComponent(shopId)}`,
    ),
  ozonAttributeValues: (input: {
    shopId: string
    categoryId: string
    typeId: string
    attributeId: string
    limit?: number
    lastValueId?: string
  }) => {
    const search = new URLSearchParams({ shopId: input.shopId })
    if (input.limit !== undefined) search.set('limit', String(input.limit))
    if (input.lastValueId) search.set('lastValueId', input.lastValueId)
    return api.get<OzonCategoryAttributeValuePage>(
      `/ozon/categories/${encodeURIComponent(input.categoryId)}/types/${encodeURIComponent(
        input.typeId,
      )}/attributes/${encodeURIComponent(input.attributeId)}/values?${search.toString()}`,
    )
  },
  syncOfferStatuses: (offerIds: string[]) =>
    api.post<Offer[]>(
      '/offers/status-sync',
      { offerIds },
      {
        idempotencyKey: idempotencyKey('offer-status-sync'),
      },
    ),
  listingGroups: (offerId?: string) =>
    api.get<Page<ListingGroup>>(
      `/listing-groups${offerId ? `?offerId=${encodeURIComponent(offerId)}` : ''}`,
    ),
  listingGroup: (id: string) => api.get<ListingGroupDetail>(`/listing-groups/${id}`),
  validate: (input: ValidationCommand) => api.post<ValidationResult>('/validation', input),
  ozonWarehouses: (shopId: string) =>
    api.get<{ items: Array<{ id: string; name: string; scheme: string }> }>(
      `/shops/${shopId}/warehouses`,
    ),
  publish: (input: {
    listingGroupId: string
    offerIds: string[]
    scenario?: string
    warehouseByShop?: Record<string, string>
    stockQuantity?: number
  }) => api.post<AsyncAccepted>('/publish', input, { idempotencyKey: idempotencyKey('publish') }),
  jobs: (status?: string) =>
    api.get<Page<Job>>(`/jobs${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  job: (id: string) => api.get<Job>(`/jobs/${id}`),
  retryJob: (id: string) =>
    api.post<AsyncAccepted>(`/jobs/${id}/retry`, undefined, {
      idempotencyKey: idempotencyKey('job-retry'),
    }),
  cancelJob: (id: string) =>
    api.post<Job>(`/jobs/${id}/cancel`, undefined, {
      idempotencyKey: idempotencyKey('job-cancel'),
    }),
  orders: (input: {
    page: number
    limit: number
    includeTotalAmount?: boolean
    search?: string
    shopId?: string
    shopIds?: string[]
    status?: string
    createdFrom?: string
    createdTo?: string
    minimumAmount?: number
    maximumAmount?: number
  }) => {
    const search = new URLSearchParams({ page: String(input.page), limit: String(input.limit) })
    if (input.includeTotalAmount !== undefined)
      search.set('includeTotalAmount', String(input.includeTotalAmount))
    if (input.search) search.set('search', input.search)
    if (input.shopId) search.set('shopId', input.shopId)
    if (input.shopIds?.length) search.set('shopIds', input.shopIds.join(','))
    if (input.status) search.set('status', input.status)
    if (input.createdFrom) search.set('createdFrom', input.createdFrom)
    if (input.createdTo) search.set('createdTo', input.createdTo)
    if (input.minimumAmount !== undefined) search.set('minimumAmount', String(input.minimumAmount))
    if (input.maximumAmount !== undefined) search.set('maximumAmount', String(input.maximumAmount))
    return api.get<Page<Order>>(`/orders?${search.toString()}`)
  },
  orderFinanceSummary: (filters: OrderFinanceFilters, refresh = false) =>
    api.get<FilteredOrderFinanceSummary>(orderFinanceSummaryPath(filters, refresh)),
  exportOrders: (input: {
    search?: string
    shopId?: string
    shopIds?: string[]
    status?: string
    createdFrom?: string
    createdTo?: string
    minimumAmount?: number
    maximumAmount?: number
  }) => {
    const search = new URLSearchParams()
    if (input.search) search.set('search', input.search)
    if (input.shopId) search.set('shopId', input.shopId)
    if (input.shopIds?.length) search.set('shopIds', input.shopIds.join(','))
    if (input.status) search.set('status', input.status)
    if (input.createdFrom) search.set('createdFrom', input.createdFrom)
    if (input.createdTo) search.set('createdTo', input.createdTo)
    if (input.minimumAmount !== undefined) search.set('minimumAmount', String(input.minimumAmount))
    if (input.maximumAmount !== undefined) search.set('maximumAmount', String(input.maximumAmount))
    const query = search.size ? `?${search.toString()}` : ''
    return rawBlobRequest(`/orders/export${query}`, {
      headers: {
        accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    })
  },
  order: (id: string) => api.get<Order>(`/orders/${id}`),
  syncOrders: (shopId: string) =>
    api.post<AsyncAccepted>(
      '/order-sync',
      { shopId },
      { idempotencyKey: idempotencyKey('order-sync') },
    ),
  updateOrder: (id: string, note: string, etag: string) =>
    api.patch<Order>(`/orders/${id}`, { note }, { ifMatch: etag }),
  updateOrderLineMapping: (
    orderId: string,
    lineId: string,
    sourceSkuId: string | null,
    etag: string,
  ) =>
    api.patch<Order>(
      `/orders/${orderId}/lines/${lineId}/mapping`,
      { sourceSkuId },
      { ifMatch: etag },
    ),
  bulkUpdateOrderLineMappings: (items: BulkOrderLineMappingItem[]) =>
    api.patch<BulkOrderLineMappingResult>('/orders/line-mappings', { items }),
  bulkUpsertOrderLineSources: (items: BulkOrderLineSourceItem[]) =>
    api.put<BulkOrderLineSourceResult>('/orders/line-sources', { items }),
  upsertOrderLineSource: (
    orderId: string,
    lineId: string,
    source: {
      sourceOrigin: OrderSourceOrigin
      sourceUrl: string
      unitCostCny: string
      sourceShippingCny: string
      discountCny: string
      manualOtherFeeCny: string
    },
    etag: string,
  ) => api.put<Order>(`/orders/${orderId}/lines/${lineId}/source`, source, { ifMatch: etag }),
  settings: () => api.get<Settings>('/settings'),
  screenBranding: () => api.get<ScreenBrandingView>('/data-screen/branding'),
  updateScreenTitle: (title: string, etag: string) =>
    api.request<ScreenBrandingView>('/data-screen/branding/title', {
      method: 'PATCH',
      ifMatch: etag,
      body: { title },
    }),
  uploadScreenLogo: (file: File, etag: string) =>
    api.request<ScreenBrandingView>('/data-screen/branding/logo', {
      method: 'POST',
      rawBody: file,
      contentType: 'application/octet-stream',
      ifMatch: etag,
      headers: { 'x-image-content-type': file.type },
    }),
  updateScreenBranding: (enabled: boolean, current: ScreenBrandingView) =>
    api.request<ScreenBrandingView>('/data-screen/branding', {
      method: 'PATCH',
      ifMatch: current.etag,
      body: { enabled, expectedMonthlyFee: current.monthlyFee, ruleVersion: current.ruleVersion },
    }),
  updateSettings: (settings: Omit<Settings, 'etag'>, etag: string) =>
    api.put<Settings>('/settings', settings, { ifMatch: etag }),
  uploadAiImageLogo: (file: File, etag: string) =>
    api.request<Settings>('/settings/ai-logo', {
      method: 'POST',
      rawBody: file,
      contentType: 'application/octet-stream',
      ifMatch: etag,
      headers: {
        'x-image-content-type': file.type,
        'x-image-file-name': encodeURIComponent(file.name),
      },
    }),
  aiSettings: () => api.get<AiSettingsView>('/settings/ai'),
  aiPointBalance: () => api.get<AiPointBalanceView>('/ai-points/balance'),
  imageRewrites: () => api.get<{ items: StandaloneImageJob[] }>('/image-rewrites'),
  uploadRewriteImage: (file: File) =>
    api.request<{ uploadId: string; imageUrl: string }>('/image-rewrites/uploads', {
      method: 'POST',
      rawBody: file,
      contentType: 'application/octet-stream',
      headers: { 'x-image-content-type': file.type },
    }),
  createImageRewrite: (input: { uploadId: string; requestId: string; prompt: string }) =>
    api.post<{ jobId: string }>('/image-rewrites', input),
  subAccounts: () => api.get<TenantSubAccountPage>('/accounts/subaccounts'),
  subAccountListingStats: (
    id: string,
    input: { range: '7d' | '30d' | 'custom'; start?: string; end?: string },
  ) => {
    const search = new URLSearchParams({ range: input.range })
    if (input.start) search.set('start', input.start)
    if (input.end) search.set('end', input.end)
    return api.get<AccountListingStats>(
      `/accounts/subaccounts/${id}/listing-stats?${search.toString()}`,
    )
  },
  createSubAccount: (input: { username: string; password: string; displayName?: string }) =>
    api.post<TenantSubAccount & { fee: number; pointBalance: number }>(
      '/accounts/subaccounts',
      input,
    ),
  updateSubAccountStatus: (id: string, status: 'ACTIVE' | 'DISABLED') =>
    api.patch<TenantSubAccount>(`/accounts/subaccounts/${id}/status`, { status }),
  updateSubAccountName: (id: string, displayName: string) =>
    api.patch<Pick<TenantSubAccount, 'id' | 'username' | 'displayName' | 'updatedAt'>>(
      `/accounts/subaccounts/${id}/name`,
      { displayName },
    ),
  updateSubAccountPassword: (id: string, password: string) =>
    api.patch<{ id: string; passwordUpdatedAt: string; revokedSessionCount: number }>(
      `/accounts/subaccounts/${id}/password`,
      { password },
    ),
  pointLedger: () => api.get<Page<AiPointLedgerItem>>('/accounts/point-ledger'),
  aiRewriteLogs: (
    filters: {
      modality?: string
      status?: string
      search?: string
      cursor?: string
      limit?: number
      listingGroupId?: string
      platform?: 'WB'
    } = {},
  ) => {
    const search = new URLSearchParams()
    if (filters.modality) search.set('modality', filters.modality)
    if (filters.status) search.set('status', filters.status)
    if (filters.search) search.set('search', filters.search)
    if (filters.cursor) search.set('cursor', filters.cursor)
    if (filters.limit) search.set('limit', String(filters.limit))
    if (filters.listingGroupId) search.set('listingGroupId', filters.listingGroupId)
    if (filters.platform) search.set('platform', filters.platform)
    const query = search.toString()
    return api.get<AiRewriteLogPage>(`/ai-rewrite-logs${query ? `?${query}` : ''}`)
  },
  aiRewriteProducts: (
    filters: {
      modality?: string
      status?: string
      search?: string
      cursor?: string
      limit?: number
    } = {},
  ) => {
    const search = new URLSearchParams()
    if (filters.modality) search.set('modality', filters.modality)
    if (filters.status) search.set('status', filters.status)
    if (filters.search) search.set('search', filters.search)
    if (filters.cursor) search.set('cursor', filters.cursor)
    if (filters.limit) search.set('limit', String(filters.limit))
    const query = search.toString()
    return api.get<AiRewriteProductPage>(`/ai-rewrite-log-products${query ? `?${query}` : ''}`)
  },
  putAiProvider: (kind: AiProviderRouteKind, input: AiProviderConfigWrite, etag: string) =>
    api.put<AiProviderConfigView>(`/settings/ai/${kind}`, input, { ifMatch: etag }),
  patchAiProvider: (
    kind: AiProviderRouteKind,
    input: Partial<AiProviderConfigWrite>,
    etag: string,
  ) => api.patch<AiProviderConfigView>(`/settings/ai/${kind}`, input, { ifMatch: etag }),
  testAiProvider: (
    kind: AiProviderRouteKind,
    scenario: 'success' | 'unauthorized' | 'rate-limited' | 'timeout' = 'success',
  ) =>
    api.post<AsyncAccepted>(
      `/settings/ai/${kind}/test`,
      { scenario },
      { idempotencyKey: idempotencyKey(`ai-${kind}-test`) },
    ),
  devices: () => api.get<Page<Device>>('/devices'),
  createPairing: (deviceName: string) =>
    api.post<PairingTicket>(
      '/pairings',
      { deviceName },
      { idempotencyKey: idempotencyKey('pairing') },
    ),
  deleteDevice: (id: string) => api.delete<void>(`/devices/${id}`),
  notifications: () => api.get<Page<TenantNotification>>('/notifications'),
  readNotification: (id: string) => api.patch<TenantNotification>(`/notifications/${id}/read`, {}),
}

export const queryKeys = {
  dashboard: ['dashboard'] as const,
  dataScreen: (filters: {
    range: DataScreenRange
    start?: string
    end?: string
    shopId?: string
  }) => ['data-screen', filters] as const,
  shops: ['shops'] as const,
  products: (search = '') => ['products', search] as const,
  storeCollections: ['store-collections'] as const,
  storeCollectionRun: ['store-collection-run'] as const,
  storeCollectionProducts: (filters: StoreCollectionProductQuery = {}) =>
    ['store-collection-products', filters] as const,
  autoSelectionConfig: ['auto-selection-config'] as const,
  seerfarCategories: ['seerfar-categories'] as const,
  autoSelectionRun: ['auto-selection-run'] as const,
  autoSelectionCandidates: (filters: AutoSelectionCandidateQuery = {}) =>
    ['auto-selection-candidates', filters] as const,
  seerfarSettings: ['seerfar-settings'] as const,
  autoSelectionAutomationSettings: ['auto-selection-automation-settings'] as const,
  product: (id: string) => ['product', id] as const,
  offers: ['offers'] as const,
  offer: (id: string) => ['offer', id] as const,
  ozonCategories: (shopId: string) => ['ozon-categories', shopId] as const,
  ozonCategoryAttributes: (shopId: string, categoryId: string, typeId: string) =>
    ['ozon-category-attributes', shopId, categoryId, typeId] as const,
  ozonAttributeValues: (shopId: string, categoryId: string, typeId: string, attributeId: string) =>
    ['ozon-attribute-values', shopId, categoryId, typeId, attributeId] as const,
  listingGroups: ['listing-groups'] as const,
  validation: ['validation'] as const,
  jobs: (status = '') => ['jobs', status] as const,
  job: (id: string) => ['job', id] as const,
  orders: ['orders'] as const,
  orderDetails: ['order'] as const,
  order: (id: string) => ['order', id] as const,
  settings: ['settings'] as const,
  aiSettings: ['ai-settings'] as const,
  aiPointBalance: ['ai-point-balance'] as const,
  subAccounts: ['tenant-sub-accounts'] as const,
  subAccountListingStats: (id: string, range: '7d' | '30d') =>
    ['tenant-sub-account-listing-stats', id, range] as const,
  pointLedger: ['tenant-point-ledger'] as const,
  aiRewriteLogs: (
    filters: {
      modality?: string
      status?: string
      search?: string
      listingGroupId?: string
      platform?: 'WB'
    } = {},
  ) => ['ai-rewrite-logs', filters] as const,
  aiRewriteProducts: (filters: { status?: string; search?: string } = {}) =>
    ['ai-rewrite-log-products', filters] as const,
  devices: ['devices'] as const,
  notifications: ['notifications'] as const,
}

export const terminalJobStatuses = new Set<Job['status']>([
  'PARTIAL_SUCCESS',
  'SUCCEEDED',
  'FAILED',
  'MANUAL_REVIEW',
  'CANCELLED',
  'DEAD_LETTER',
])
